/**
 * Auth Store factory.
 *
 * Manages authentication state for Cognito-based auth. Stores the user
 * profile + ID/refresh tokens, persisted via the supplied StorageAdapter.
 *
 * Note: Cognito SDK manages its own internal token storage on top of this;
 * the `idToken` here is what gets bridged to API calls.
 *
 * IMPORTANT: Call this **exactly once per app process**. The returned hook
 * is a singleton; calling the factory twice produces two independent stores
 * with separate state, listeners, and persisted slices — a recipe for
 * silent state divergence. Compose at module top-level (see apps/web/src/
 * stores/authStore.ts) and re-export the hook plus selectors.
 */
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type {
  CurrentUserDto,
  ScanQuotaDto,
  SystemRole,
  UserProDto,
} from '@splitfiesta/shared';

import type { StorageAdapter } from '../platform/storage';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  profileImage?: string | null;
  systemRole?: SystemRole;
  isEmailVerified: boolean;
  isActive: boolean;
  termsAcceptedAt?: string;
  /** Set while an account deletion is scheduled (30-day grace window). */
  deletionEffectiveAt?: string;
  createdAt: string;
  settings: {
    currency?: string;
    language?: string;
    /** The user hid the example group; undefined = never toggled. */
    exampleGroupHidden?: boolean;
  };
  /** Optional: a persisted store from before Pro has no value. */
  pro?: UserProDto;
  /** null = not subject to the quota; undefined = not known yet. */
  scanQuota?: ScanQuotaDto | null;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasHydrated: boolean;
  idToken: string | null;
  refreshToken: string | null;
}

interface AuthActions {
  setAuth: (user: AuthUser, idToken: string, refreshToken?: string) => void;
  updateUser: (userData: Partial<AuthUser>) => void;
  setIdToken: (idToken: string) => void;
  setRefreshToken: (refreshToken: string) => void;
  clearAuth: () => void;
  logout: () => void;
  initialize: () => void;
  setHasHydrated: (state: boolean) => void;
  setAuthFromDto: (
    dto: AuthUserSource,
    idToken: string,
    refreshToken?: string
  ) => void;
}

export type AuthStore = AuthState & AuthActions;

/** What both `/auth/me` and `/auth/sync` carry for the stored user. */
export type AuthUserSource = Pick<
  CurrentUserDto,
  | 'id'
  | 'email'
  | 'name'
  | 'profileImage'
  | 'systemRole'
  | 'isEmailVerified'
  | 'isActive'
  | 'termsAcceptedAt'
  | 'deletionEffectiveAt'
  | 'createdAt'
  | 'settings'
  | 'pro'
  | 'scanQuota'
>;

/**
 * `previous` is the current store user: an older `/auth/me` response or a
 * pre-Pro bundle's DTO has no `pro` or `scanQuota`, and that must not wipe
 * the known value.
 * Only the same user's value carries over.
 */
const dtoToAuthUser = (
  dto: AuthUserSource,
  previous?: AuthUser | null
): AuthUser => ({
  id: dto.id,
  email: dto.email,
  name: dto.name,
  profileImage: dto.profileImage,
  systemRole: dto.systemRole,
  isEmailVerified: dto.isEmailVerified,
  isActive: dto.isActive,
  termsAcceptedAt: dto.termsAcceptedAt,
  deletionEffectiveAt: dto.deletionEffectiveAt,
  createdAt: dto.createdAt,
  settings: {
    currency: dto.settings.currency,
    language: dto.settings.language,
    exampleGroupHidden: dto.settings.exampleGroupHidden,
  },
  pro: dto.pro ?? (previous?.id === dto.id ? previous.pro : undefined),
  // Not `??`: a server null (not subject) must replace a stored quota.
  scanQuota:
    dto.scanQuota !== undefined
      ? dto.scanQuota
      : previous?.id === dto.id
        ? previous.scanQuota
        : undefined,
});

export interface CreateAuthStoreDeps {
  storage: StorageAdapter;
}

export function createAuthStore(deps: CreateAuthStoreDeps) {
  return create<AuthStore>()(
    devtools(
      persist(
        immer((set, get) => ({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          hasHydrated: false,
          idToken: null,
          refreshToken: null,

          setAuth: (user, idToken, refreshToken) => {
            set(state => {
              state.user = user;
              state.idToken = idToken;
              if (refreshToken) {
                state.refreshToken = refreshToken;
              }
              state.isAuthenticated = true;
              state.isLoading = false;
            });
          },

          updateUser: userData => {
            set(state => {
              if (state.user) {
                const { id: _id, ...safeData } = userData;
                Object.assign(state.user, safeData);
              }
            });
          },

          setIdToken: idToken => {
            set(state => {
              state.idToken = idToken;
            });
          },

          setRefreshToken: refreshToken => {
            set(state => {
              state.refreshToken = refreshToken;
            });
          },

          clearAuth: () => {
            set(state => {
              state.user = null;
              state.idToken = null;
              state.refreshToken = null;
              state.isAuthenticated = false;
              state.isLoading = false;
            });
          },

          logout: () => {
            set(state => {
              state.user = null;
              state.idToken = null;
              state.refreshToken = null;
              state.isAuthenticated = false;
              state.isLoading = false;
            });
          },

          initialize: () => {
            set(state => {
              state.isLoading = false;
            });
          },

          setHasHydrated: hasHydrated => {
            set(state => {
              state.hasHydrated = hasHydrated;
            });
          },

          setAuthFromDto: (dto, idToken, refreshToken) => {
            const previous = get().user;
            set(state => {
              state.user = dtoToAuthUser(dto, previous);
              state.idToken = idToken;
              if (refreshToken) {
                state.refreshToken = refreshToken;
              }
              state.isAuthenticated = true;
              state.isLoading = false;
            });
          },
        })),
        {
          name: 'auth-storage',
          storage: createJSONStorage(() => deps.storage),
          partialize: state => ({
            user: state.user,
            idToken: state.idToken,
            refreshToken: state.refreshToken,
            isAuthenticated: state.isAuthenticated,
          }),
          onRehydrateStorage: () => state => {
            state?.setHasHydrated(true);
          },
        }
      ),
      { name: 'auth-store' }
    )
  );
}
